// src/components/User/Login.jsx
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { authService } from '../../services/authService';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useUser();
  const [step, setStep] = useState('EMAIL'); // 'EMAIL' | 'PASSWORD' | 'OTP' | 'DETAILS' | 'FORGOT_RESET'
  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState(''); // Mobile number
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form Validation for Mobile Number (Starts with 6-9, exactly 10 digits)
  const isPhone = /^[6-9]\d{9}$/.test(identifier);
  const isIdentifierValid = isPhone;
  const isPasswordValid = password.length >= 6;
  const isOtpValid = otp.length === 6; 
  const isDetailsValid = name.length > 2 && password.length >= 6 && password === confirmPassword;

  const extractToken = (res) => {
    if (!res) return null;
    let potentialToken = null;
    if (typeof res === 'string') potentialToken = res;
    else if (typeof res === 'object') {
      potentialToken = res.token || res.jwt || res.accessToken || res.data?.token || res.data?.jwt;
    }
    if (typeof potentialToken === 'string' && potentialToken.startsWith('ey')) {
      return potentialToken;
    }
    return potentialToken || 'dummy_token_' + Date.now(); 
  };

  const handleCheckIdentifier = async () => {
    setLoading(true);
    setError('');
    try {
      const responseData = await authService.checkPhoneNumber(identifier);
      
      const resString = typeof responseData === 'string' ? responseData.toLowerCase() : '';
      
      // Safely checking if the response explicitly states "not found"
      if (resString.includes('not found')) {
        await authService.sendSmsOtp(identifier);
        setStep('OTP');
      } else {
        // If it exists or returns true, proceed to PASSWORD step safely
        setStep('PASSWORD');
      }
    } catch (err) {
      // Safely handle error response data to prevent '.includes is not a function' crash
      const errData = err.response?.data;
      const errString = typeof errData === 'string' ? errData.toLowerCase() : '';
      
      if (
        err.response?.status === 409 || 
        errString.includes('already exist') || 
        errData?.message?.toLowerCase().includes('already exist')
      ) {
        setStep('PASSWORD');
      } else if (
        err.response?.status === 404 || 
        errString.includes('not found') || 
        errData?.message?.toLowerCase().includes('not found')
      ) {
        try {
          await authService.sendSmsOtp(identifier);
          setStep('OTP');
        } catch (otpErr) {
          setError("Failed to send OTP. Please try again.");
        }
      } else {
        setError("Something went wrong. Please check your connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await authService.login({ identifier, password });
      
      const token = extractToken(data);
      if (!token) {
        setError("Login failed: Could not retrieve a valid token.");
        setLoading(false);
        return;
      }

      const userData = {
        id: data.id || Date.now(),
        name: data.name || 'User',
        email: data.email || '',
        phoneNo: identifier,
        token: token,
      };

      login(userData);
      
      const from = location.state?.from?.pathname || '/quick';
      navigate(from, { replace: true });
    } catch (error) {
      setError("Invalid Password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        otp,
        purpose: "REGISTER",
        phoneNumber: identifier
      };
      await authService.verifyOtp(payload);
      setStep('DETAILS');
    } catch (err) {
      setError("Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        name,
        password,
        phoneNo: identifier
      };
      await authService.register(payload);
      
      const loginData = await authService.login({ identifier, password });
      const token = extractToken(loginData);
      
      if (!token) {
        setError("Registration Successful, but auto-login failed. Please login manually.");
        setStep('PASSWORD');
        setLoading(false);
        return;
      }

      const userData = {
        id: loginData.id || Date.now(),
        name: name,
        email: loginData.email || '',
        phoneNo: identifier,
        token: token,
      };

      login(userData);
      
      const from = location.state?.from?.pathname || '/quick';
      navigate(from, { replace: true });
    } catch (error) {
      setError("Registration Failed. Invalid details.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setError('');
    try {
      // Trigger the forgot password API which directly sends the OTP via SMS
      await authService.forgotPasswordSms(identifier);
      setOtp('');
      setPassword('');
      setConfirmPassword('');
      setStep('FORGOT_RESET');
    } catch (err) {
      setError("Failed to send reset OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setLoading(true);
    setError('');
    try {
      await authService.resetPasswordSms({
        phoneNumber: identifier,
        otp,
        newPassword: password
      });
      alert('Password reset successful! Please login with your new password.');
      setStep('PASSWORD');
      setPassword('');
      setConfirmPassword('');
      setOtp('');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data || "Failed to reset password. Invalid OTP or request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-[#f0f7f0] px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-gray-100 overflow-hidden">
        
        <div className="bg-[#FFC20E] border-b-4 border-[#008A44] py-8 px-6 flex flex-col items-center justify-center rounded-b-[40px] mb-8 relative">
          <img src="/web/icon-192.png" alt="SV Mart" className="w-20 h-20 object-contain mb-3 drop-shadow-md rounded-full bg-white p-2" />
          <h1 className="text-3xl font-black italic tracking-widest text-[#E32726] mb-1" style={{ textShadow: '-1.5px -1.5px 0 #fff, 1.5px -1.5px 0 #fff, -1.5px 1.5px 0 #fff, 1.5px 1.5px 0 #fff' }}>
            SV MART
          </h1>
          <p className="text-[#008A44] font-extrabold text-center text-[11px] tracking-widest uppercase bg-white/90 px-4 py-1.5 rounded-full mt-1 shadow-sm">
            Instant Groceries & More
          </p>
        </div>

        <div className="px-8 pb-8">
          {error && (
            <div className="bg-red-50 text-red-500 p-3 rounded-xl text-sm font-medium border border-red-100 mb-6 flex items-center gap-2">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          {step === 'EMAIL' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Welcome</h2>
              <p className="text-gray-500 font-medium mb-6 text-sm">Enter your mobile number</p>
              
              <div className="flex items-center border border-gray-200 rounded-2xl px-4 h-14 bg-gray-50 mb-6 focus-within:border-[#E32726] focus-within:bg-white transition-colors">
                <i className="fas fa-phone-alt text-gray-400 mr-3 text-lg"></i>
                <input 
                  type="tel" 
                  maxLength="10"
                  value={identifier} 
                  onChange={(e) => { 
                    setIdentifier(e.target.value.replace(/[^0-9]/g, '')); 
                    setError(''); 
                  }} 
                  placeholder="Mobile Number" 
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 font-bold text-base h-full placeholder-gray-400"
                  disabled={loading}
                />
              </div>

              <button 
                onClick={handleCheckIdentifier} 
                disabled={!isIdentifierValid || loading} 
                className={`w-full h-14 rounded-2xl flex items-center justify-center shadow-md transition-all ${isIdentifierValid ? 'bg-[#E32726] hover:bg-[#c92222] text-white cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
              >
                {loading ? <i className="fas fa-spinner fa-spin text-xl"></i> : <span className="font-black text-lg tracking-wide">Continue</span>}
              </button>
            </div>
          )}

          {step === 'PASSWORD' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Welcome Back</h2>
              <p className="text-gray-500 font-medium mb-6 text-sm">+91 {identifier}</p>
              
              <div className="flex items-center border border-gray-200 rounded-2xl px-4 h-14 bg-gray-50 mb-3 focus-within:border-[#E32726] focus-within:bg-white transition-colors">
                <i className="fas fa-lock text-gray-400 mr-3 text-lg"></i>
                <input 
                  type={showPassword ? "text" : "password"} 
                  value={password} 
                  onChange={(e) => { setPassword(e.target.value); setError(''); }} 
                  placeholder="Password" 
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 font-bold text-base h-full placeholder-gray-400"
                  disabled={loading}
                  onKeyDown={(e) => e.key === 'Enter' && isPasswordValid && handleLogin()}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-600 p-2">
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>

              <div className="flex justify-end mb-6">
                 <button type="button" onClick={handleForgotPassword} className="text-[#E32726] hover:text-[#c92222] font-bold text-sm transition-colors">
                   Forgot Password?
                 </button>
              </div>

              <button 
                onClick={handleLogin} 
                disabled={!isPasswordValid || loading} 
                className={`w-full h-14 rounded-2xl flex items-center justify-center shadow-md transition-all ${isPasswordValid ? 'bg-[#E32726] hover:bg-[#c92222] text-white cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
              >
                {loading ? <i className="fas fa-spinner fa-spin text-xl"></i> : <span className="font-black text-lg tracking-wide">Login</span>}
              </button>

              <div className="text-center mt-6">
                <button onClick={() => { setStep('EMAIL'); setPassword(''); setError(''); }} className="text-gray-500 hover:text-[#E32726] font-bold text-sm transition-colors">
                  Change Details
                </button>
              </div>
            </div>
          )}

          {step === 'OTP' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Verify OTP</h2>
              <p className="text-gray-500 font-medium mb-6 text-sm">Enter the 6-digit OTP sent to +91 {identifier}</p>
              
              <div className="relative flex justify-between mb-8 gap-2">
                {[0, 1, 2, 3, 4, 5].map((index) => {
                  const isActive = otp.length === index || (otp.length === 6 && index === 5);
                  return (
                    <div 
                      key={index} 
                      className={`w-10 sm:w-12 h-12 sm:h-14 border-2 rounded-xl flex items-center justify-center bg-gray-50 ${isActive ? 'border-[#E32726] bg-red-50/30' : 'border-gray-200'}`}
                    >
                      <span className="text-lg sm:text-xl font-black text-gray-900">{otp[index] || ''}</span>
                    </div>
                  );
                })}
                <input 
                  type="text" 
                  maxLength="6" 
                  value={otp} 
                  onChange={(e) => { setOtp(e.target.value.replace(/[^0-9]/g, '')); setError(''); }} 
                  className="absolute inset-0 w-full h-full opacity-0 cursor-text"
                  autoFocus 
                />
              </div>

              <button 
                onClick={handleVerifyOtp} 
                disabled={!isOtpValid || loading} 
                className={`w-full h-14 rounded-2xl flex items-center justify-center shadow-md transition-all mb-4 ${isOtpValid ? 'bg-[#E32726] hover:bg-[#c92222] text-white cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
              >
                {loading ? <i className="fas fa-spinner fa-spin text-xl"></i> : <span className="font-black text-lg tracking-wide">Verify OTP</span>}
              </button>

              <div className="text-center mt-4">
                <button onClick={() => { setStep('EMAIL'); setOtp(''); setError(''); }} className="text-gray-500 hover:text-[#E32726] font-bold text-sm transition-colors">
                  Change Details
                </button>
              </div>
            </div>
          )}

          {step === 'DETAILS' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Complete Profile</h2>
              <p className="text-gray-500 font-medium mb-6 text-sm">Set your name and password</p>
              
              <div className="flex items-center border border-gray-200 rounded-2xl px-4 h-14 bg-gray-50 mb-4 focus-within:border-[#E32726] focus-within:bg-white transition-colors">
                <i className="fas fa-user text-gray-400 mr-3 text-lg"></i>
                <input 
                  type="text" 
                  value={name} 
                  onChange={(e) => { setName(e.target.value); setError(''); }} 
                  placeholder="Full Name" 
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 font-bold text-base h-full placeholder-gray-400"
                  disabled={loading}
                />
              </div>

              <div className="flex items-center border border-gray-200 rounded-2xl px-4 h-14 bg-gray-50 mb-4 focus-within:border-[#E32726] focus-within:bg-white transition-colors">
                <i className="fas fa-lock text-gray-400 mr-3 text-lg"></i>
                <input 
                  type={showPassword ? "text" : "password"} 
                  value={password} 
                  onChange={(e) => { setPassword(e.target.value); setError(''); }} 
                  placeholder="Create Password" 
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 font-bold text-base h-full placeholder-gray-400"
                  disabled={loading}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-600 p-2">
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>

              <div className="flex items-center border border-gray-200 rounded-2xl px-4 h-14 bg-gray-50 mb-6 focus-within:border-[#E32726] focus-within:bg-white transition-colors">
                <i className="fas fa-lock text-gray-400 mr-3 text-lg"></i>
                <input 
                  type={showConfirmPassword ? "text" : "password"} 
                  value={confirmPassword} 
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }} 
                  placeholder="Verify Password" 
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 font-bold text-base h-full placeholder-gray-400"
                  disabled={loading}
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="text-gray-400 hover:text-gray-600 p-2">
                  <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>

              <button 
                onClick={handleRegister} 
                disabled={!isDetailsValid || loading} 
                className={`w-full h-14 rounded-2xl flex items-center justify-center shadow-md transition-all ${isDetailsValid ? 'bg-[#E32726] hover:bg-[#c92222] text-white cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
              >
                {loading ? <i className="fas fa-spinner fa-spin text-xl"></i> : <span className="font-black text-lg tracking-wide">Register & Login</span>}
              </button>
            </div>
          )}

          {step === 'FORGOT_RESET' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Reset Password</h2>
              <p className="text-gray-500 font-medium mb-6 text-sm">Enter the OTP sent to +91 {identifier} and your new password</p>
              
              <div className="flex items-center border border-gray-200 rounded-2xl px-4 h-14 bg-gray-50 mb-4 focus-within:border-[#E32726] focus-within:bg-white transition-colors">
                <i className="fas fa-comment-dots text-gray-400 mr-3 text-lg"></i>
                <input 
                  type="text" 
                  maxLength="6"
                  value={otp} 
                  onChange={(e) => { setOtp(e.target.value.replace(/[^0-9]/g, '')); setError(''); }} 
                  placeholder="Enter 6-digit OTP" 
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 font-bold text-base h-full placeholder-gray-400"
                  disabled={loading}
                />
              </div>

              <div className="flex items-center border border-gray-200 rounded-2xl px-4 h-14 bg-gray-50 mb-4 focus-within:border-[#E32726] focus-within:bg-white transition-colors">
                <i className="fas fa-lock text-gray-400 mr-3 text-lg"></i>
                <input 
                  type={showPassword ? "text" : "password"} 
                  value={password} 
                  onChange={(e) => { setPassword(e.target.value); setError(''); }} 
                  placeholder="New Password" 
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 font-bold text-base h-full placeholder-gray-400"
                  disabled={loading}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-600 p-2">
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>

              <div className="flex items-center border border-gray-200 rounded-2xl px-4 h-14 bg-gray-50 mb-6 focus-within:border-[#E32726] focus-within:bg-white transition-colors">
                <i className="fas fa-lock text-gray-400 mr-3 text-lg"></i>
                <input 
                  type={showConfirmPassword ? "text" : "password"} 
                  value={confirmPassword} 
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }} 
                  placeholder="Confirm New Password" 
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 font-bold text-base h-full placeholder-gray-400"
                  disabled={loading}
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="text-gray-400 hover:text-gray-600 p-2">
                  <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>

              <button 
                onClick={handleResetPassword} 
                disabled={!(otp.length === 6 && password.length >= 6 && password === confirmPassword) || loading} 
                className={`w-full h-14 rounded-2xl flex items-center justify-center shadow-md transition-all ${otp.length === 6 && password.length >= 6 && password === confirmPassword ? 'bg-[#E32726] hover:bg-[#c92222] text-white cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
              >
                {loading ? <i className="fas fa-spinner fa-spin text-xl"></i> : <span className="font-black text-lg tracking-wide">Reset Password</span>}
              </button>
              
              <div className="text-center mt-6">
                <button onClick={() => { setStep('PASSWORD'); setPassword(''); setConfirmPassword(''); setOtp(''); setError(''); }} className="text-gray-500 hover:text-[#E32726] font-bold text-sm transition-colors">
                  Back to Login
                </button>
              </div>
            </div>
          )}

        </div>

        <div className="bg-gray-50 border-t border-gray-100 p-6 flex flex-col items-center justify-center">
          <p className="text-gray-400 font-bold text-xs uppercase mb-4 tracking-wider">Or bypass login</p>
          <Link to="/" className="flex items-center bg-white px-6 py-3.5 rounded-full shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors">
            <i className="fas fa-user-circle text-gray-400 text-xl"></i>
            <span className="text-gray-600 font-bold text-sm ml-2">Continue as Guest</span>
          </Link>
        </div>
        
      </div>
    </div>
  );
};

export default Login;